/**
 * WhatsApp Embedded Signup launcher (Tech Provider flow).
 *
 * Loads the Facebook JS SDK and opens the Embedded Signup popup so a vendor can
 * authorize their OWN WhatsApp Business Account. On success it returns:
 *   { code, phoneNumberId, wabaId }
 * which the dashboard POSTs to the backend to finish onboarding.
 *
 * Required env (frontend/.env, set at build time):
 *   REACT_APP_FACEBOOK_APP_ID    - the Meta app id
 *   REACT_APP_WHATSAPP_CONFIG_ID - the Embedded Signup configuration id
 * Optional:
 *   REACT_APP_FACEBOOK_GRAPH_VERSION (default v20.0)
 */

const FB_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID;
const CONFIG_ID = process.env.REACT_APP_WHATSAPP_CONFIG_ID;
const GRAPH_VERSION = process.env.REACT_APP_FACEBOOK_GRAPH_VERSION || "v20.0";

let sdkPromise = null;

export const isEmbeddedSignupConfigured = () =>
  Boolean(FB_APP_ID) && Boolean(CONFIG_ID);

/** Inject and initialize the Facebook JS SDK exactly once. */
const loadFacebookSdk = () => {
  if (sdkPromise) return sdkPromise;

  sdkPromise = new Promise((resolve, reject) => {
    if (!FB_APP_ID) {
      reject(new Error("REACT_APP_FACEBOOK_APP_ID is not set."));
      return;
    }
    if (typeof window === "undefined") {
      reject(new Error("Embedded Signup requires a browser environment."));
      return;
    }
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = function fbAsyncInit() {
      window.FB.init({
        appId: FB_APP_ID,
        autoLogAppEvents: true,
        xfbml: false,
        version: GRAPH_VERSION,
      });
      resolve(window.FB);
    };

    const existing = document.getElementById("facebook-jssdk");
    if (existing) {
      // Script tag present but FB not ready yet; fbAsyncInit will resolve.
      return;
    }

    const script = document.createElement("script");
    script.id = "facebook-jssdk";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.src = `https://connect.facebook.net/en_US/sdk.js`;
    script.onerror = () => reject(new Error("Failed to load the Facebook SDK."));
    document.body.appendChild(script);
  });

  return sdkPromise;
};

/**
 * Open the Embedded Signup popup.
 * Resolves with { code, phoneNumberId, wabaId } once the vendor finishes.
 */
export const launchWhatsAppEmbeddedSignup = async () => {
  if (!CONFIG_ID) {
    throw new Error("REACT_APP_WHATSAPP_CONFIG_ID is not set.");
  }

  const FB = await loadFacebookSdk();

  return new Promise((resolve, reject) => {
    let signupData = null; // { phone_number_id, waba_id } from the message event

    const messageListener = (event) => {
      if (
        event.origin !== "https://www.facebook.com" &&
        event.origin !== "https://web.facebook.com"
      ) {
        return;
      }
      try {
        const data = JSON.parse(event.data);
        if (data.type === "WA_EMBEDDED_SIGNUP") {
          if (data.event === "FINISH" || data.event === "FINISH_ONLY_WABA") {
            signupData = data.data || {};
          } else if (data.event === "CANCEL") {
            cleanup();
            reject(new Error("Embedded Signup was cancelled."));
          }
        }
      } catch (err) {
        // Non-JSON messages from the SDK are expected; ignore.
      }
    };

    const cleanup = () => window.removeEventListener("message", messageListener);

    window.addEventListener("message", messageListener);

    FB.login(
      (response) => {
        cleanup();
        const code = response?.authResponse?.code;
        if (!code) {
          reject(new Error("Embedded Signup did not return an authorization code."));
          return;
        }
        if (!signupData?.phone_number_id || !signupData?.waba_id) {
          reject(
            new Error("Embedded Signup did not return the phone number / WABA details."),
          );
          return;
        }
        resolve({
          code,
          phoneNumberId: signupData.phone_number_id,
          wabaId: signupData.waba_id,
          metaBusinessId: signupData.business_id,
        });
      },
      {
        config_id: CONFIG_ID,
        response_type: "code",
        override_default_response_type: true,
        extras: { setup: {}, featureType: "", sessionInfoVersion: "3" },
      },
    );
  });
};
